import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal, findDOMNode } from 'react-dom';
import { Input } from 'zarm';

import UItem from '../item';
import { clearObj, toTrim, getFormId } from '../../utils/tools';
import { BaseWidgetProps } from '../../utils/type';
import {
  isEmail,
  regEmailAbbr,
  getPopperList,
} from './_util';

export interface EmailProps extends BaseWidgetProps {
  label?: string;
  placeholder?: string;
  readonly?: boolean;
  readOnly?: boolean;
  refreshPrice?: boolean;
  warnRules?: any;
  rules?: any[];
  validate?: {
    rules: any[];
  };
  value?: number | string;
  defaultValue?: number | string;
  disabled?: boolean;
  clearable?: boolean;
}

export interface EmailState {
  value: string;
  valueAbbr: string;
  prevValue?: string;
  visible: boolean;
  popperStyle?: any;
}

const getValue = (value: any) => {
  let val: string = '';
  let valueAbbr: string = '';

  if (value) {
    val = toTrim(value).toString();

    if (val && val.match(regEmailAbbr)) {
      [valueAbbr] = (val.match(regEmailAbbr) || []);
    }
  }

  return {
    value: val,
    valueAbbr,
  };
};

let changeTimer: any = null;
let timer: any = null;

class Email extends React.PureComponent<EmailProps, EmailState> {
  static propTypes = {
    prefixCls: PropTypes.string,
    onChange: PropTypes.func,
  };

  static defaultProps = {
    prefixCls: 'crm-email',
    onChange: () => { },
  };

  state: EmailState = {
    ...getValue(this.props.value || this.props.defaultValue),
    popperStyle: {},
    visible: false,
  };

  // 点击气泡列表状态
  isClickPopper = false;

  inputRef: any = createRef();

  popperRef: any = createRef();

  static getDerivedStateFromProps(nextProps: EmailProps, state: EmailState) {
    if ('value' in nextProps && nextProps.value !== state.prevValue) {
      return {
        ...getValue(nextProps.value),
        prevValue: nextProps.value,
      };
    }

    return null;
  }

  onChange = (value: string) => {
    const stateValue: any = getValue(value);

    this.isClickPopper = false;

    if (!value) {
      stateValue.visible = false;
    } else if (!isEmail(value)) {
      stateValue.visible = true;

      changeTimer && clearTimeout(changeTimer);

      changeTimer = setTimeout(() => {
        this.getPopperStyle();
      }, 100);
    }

    this.setState(stateValue);
  };

  onBlur = (value: string) => {
    const { onChange, ...rest } = this.props;

    this.setState({ visible: false });

    if (this.isClickPopper) {
      return false;
    }

    if (onChange) {
      onChange({
        ...rest,
        value,
      });
    }
  };

  onSelected = (event: any, value: string) => {
    const { onChange, ...rest } = this.props;
    const { _crengine } = this.props;
    const { form, dataBind, path } = _crengine || {};

    event && event.preventDefault();

    this.isClickPopper = true;
    this.setState({ visible: false, value });

    timer && clearTimeout(timer);

    timer = setTimeout(() => {
      if (form) {
        const names = getFormId(dataBind, path);

        form.setFieldsValue({
          [names]: value,
        });
      }

      if (onChange) {
        onChange({
          ...rest,
          value,
        });
      }
    }, 300);
  };

  // 获取气泡样式
  getPopperStyle = () => {
    if (
      this.inputRef
      && this.inputRef.current
      && this.popperRef
      && this.popperRef.current
    ) {
      // eslint-disable-next-line react/no-find-dom-node
      const reference: any = findDOMNode(this.inputRef.current);
      // eslint-disable-next-line react/no-find-dom-node
      const popperNode: any = findDOMNode(this.popperRef.current);

      if (!reference) {
        return false;
      }

      const { left, bottom, height } = reference.getBoundingClientRect();
      // 滚动条Y
      const scrollY = (window.scrollY || document.documentElement.scrollTop);
      // 内容高度
      const bodyHeight = Math.max(document.body.offsetHeight, document.documentElement.offsetHeight, document.documentElement.clientHeight);
      // 内容高度
      const viewWidth = Math.max(window.innerWidth, document.documentElement.clientWidth, document.body.clientWidth);
      const popperTop = (bottom + scrollY);
      // 离底部距离
      const referenceBottom = (bodyHeight - popperTop);
      const popperStyle = {
        top: popperTop,
        left,
        width: (viewWidth - left),
      };

      // 气泡定位方向
      if (popperNode) {
        const { height: popperHeight } = popperNode.getBoundingClientRect();

        if (popperHeight > (referenceBottom - 50)) {
          popperStyle.top = (popperTop - height - popperHeight);
        }
      }

      this.setState({ popperStyle });
    }
  };

  renderTooltip() {
    const { visible, popperStyle, value, valueAbbr } = this.state;
    const { prefixCls } = this.props;
    const cls = `${prefixCls}-tooltip`;
    const list = getPopperList(value);

    const tooltip = (
      <div
        className={cls}
        style={popperStyle}
        ref={this.popperRef}
      >
        {
          list.map(({ label, key }) => {
            return (
              <div
                className={`${cls}-item`}
                key={key}
                onClick={(event) => this.onSelected(event, `${valueAbbr}${label}`)}
              >
                {valueAbbr}{label}
              </div>
            );
          })
        }
      </div>
    );

    return visible ? createPortal(tooltip, document.body) : null;
  }

  render() {
    const { value: inputValue } = this.state;
    const {
      prefixCls,
      value,
      disabled,
      defaultValue,
      clearable,
      placeholder,
      readOnly,
      readonly,
      _crengine,
      onChange,
      ...rest
    } = this.props;
    const newReadOnly = (readOnly || readonly);
    const { form } = _crengine || {};

    const inputProps = clearObj({
      value: inputValue,
      disabled,
      clearable,
      placeholder,
      readOnly: newReadOnly,
      onBlur: this.onBlur,
      onChange: this.onChange,
    });

    // 如果wform存在 则去除input组件的某些属性
    if (form) {
      delete inputProps.value;
      delete inputProps.defaultValue;
      delete inputProps.onBlur;
      delete inputProps.onChange;
    }

    return (
      <UItem
        itemType="email"
        {...rest}
        readOnly={newReadOnly}
        description={this.renderTooltip()}
        _crengine={_crengine}
        initialValue={inputValue}
        validateTrigger="onBlur"
        onChange={this.onChange}
        onBlur={this.onBlur}
      >
        <Input {...inputProps} ref={this.inputRef} />
      </UItem>
    );
  }
}

export default Email;
